import { useEffect, useState } from 'react';

const DEFAULT_REASON = 'No longer using the app';

export default function DeleteAccountModal({ open, onClose, onConfirm, loading = false }) {
  const [reason, setReason] = useState(DEFAULT_REASON);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose, loading]);

  useEffect(() => {
    if (!open) setReason(DEFAULT_REASON);
  }, [open]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    onConfirm?.(reason.trim() || DEFAULT_REASON);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-[460px] rounded-3xl bg-white p-6 shadow-2xl sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="delete-account-title" className="text-xl font-bold text-[#1B1B1B] sm:text-2xl">
            Are you sure?
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D9D9D9] text-[#666666] transition hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[#666666] sm:text-base">
          This will permanently archive your account and remove your personal information. You will
          be logged out immediately.
        </p>

        <form onSubmit={handleSubmit} className="mt-6">
          <label htmlFor="delete-account-reason" className="text-sm font-medium text-[#1B1B1B]">
            Reason for leaving (optional)
          </label>
          <textarea
            id="delete-account-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            disabled={loading}
            className="mt-2 w-full resize-none rounded-xl border border-[#D9D9D9] px-4 py-3 text-sm text-[#1B1B1B] outline-none transition placeholder:text-[#AAAAAA] focus:border-[#FF595E] focus:ring-1 focus:ring-[#FF595E] disabled:cursor-not-allowed disabled:opacity-60"
          />

          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="min-w-[130px] rounded-full border border-[#666666] px-6 py-2.5 text-sm font-semibold text-[#666666] transition hover:bg-[#F8F8F8] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="min-w-[130px] rounded-full bg-[#FF595E] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Deactivating...' : 'Yes, Deactivate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
